import { useState, useEffect } from 'react';
import axios from 'axios';
import { CalendarClock, CheckCircle2, XCircle, Loader2, MinusCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  completed: { icon: CheckCircle2, cls: 'text-[#00C48C]', label: 'Completed' },
  failed: { icon: XCircle, cls: 'text-red-600', label: 'Failed' },
  running: { icon: Loader2, cls: 'text-[#1E5EFF] animate-spin', label: 'Running' },
  skipped_duplicate: { icon: MinusCircle, cls: 'text-slate-400', label: 'Skipped (already ran today)' },
};

const fmtTime = (iso) => new Date(iso).toLocaleString('en-NG', { timeZone: 'Africa/Lagos', dateStyle: 'medium', timeStyle: 'short' });

const RunRow = ({ run }) => {
  const s = STATUS_STYLES[run.status] || STATUS_STYLES.running;
  const Icon = s.icon;
  const debits = run.results?.auto_debits?.processed;
  const lowBal = run.results?.low_balance;
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-white border border-slate-200" data-testid={`cron-run-${run.run_id}`}>
      <Icon size={18} className={`mt-0.5 shrink-0 ${s.cls}`} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-semibold text-[#0B1F3A]">
            {run.job === 'low_balance' ? 'Low-balance alerts' : 'Auto-debit run'} <span className="font-normal text-slate-500">· {fmtTime(run.started_at)}</span>
          </p>
          <span className="text-xs text-slate-500" data-testid="cron-run-status">{s.label}</span>
        </div>
        {lowBal && (
          <p className="text-xs text-slate-600 mt-1" data-testid="cron-run-summary">
            {lowBal.alerts_sent} {lowBal.alerts_sent === 1 ? 'alert' : 'alerts'} sent · {lowBal.users_with_dues} {lowBal.users_with_dues === 1 ? 'member' : 'members'} due on {lowBal.due_date}
          </p>
        )}
        {debits && (
          <p className="text-xs text-slate-600 mt-1" data-testid="cron-run-summary">
            {debits.successful} debited · {debits.failed_with_penalty} penalties · {debits.skipped} skipped · {run.results?.reminders?.reminders_sent ?? 0} reminders · {run.results?.defaults?.defaults_found ?? 0} defaults
          </p>
        )}
        {run.errors?.length > 0 && <p className="text-xs text-red-600 mt-1">{run.errors.join('; ')}</p>}
      </div>
    </div>
  );
};

export const AutoRunHistory = ({ headers }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API_URL}/api/scheduler/cron-runs`, { headers }).then(r => setData(r.data)).catch(() => setData({ runs: [], error: true }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="p-4 rounded-xl bg-[#0B1F3A]/5 border-2 border-[#0B1F3A]/15" data-testid="auto-run-history">
      <div className="flex items-center gap-2 mb-1"><CalendarClock size={20} className="text-[#1E5EFF]" /><h4 className="font-bold text-[#0B1F3A]">Automatic Runs</h4></div>
      <p className="text-sm text-slate-600 mb-3" data-testid="auto-run-schedule">{data?.schedule || 'Auto-debits daily at 6:00 AM WAT · Low-balance alerts daily at 7:00 PM WAT'}</p>
      {!data ? <p className="text-sm text-slate-400">Loading...</p> : data.error ? (
        <p className="text-sm text-red-600" data-testid="auto-run-error">Couldn't load run history. Please refresh to try again.</p>
      ) : data.runs.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="auto-run-empty">No automatic runs yet. The first one runs at the next 6:00 AM WAT.</p>
      ) : (
        <div className="space-y-2">{data.runs.map(r => <RunRow key={r.run_id} run={r} />)}</div>
      )}
    </div>
  );
};
